import { X } from "@phosphor-icons/react";

export function FormField({
  label,
  value,
  onChange,
  type = "text",
  required,
  testId,
  placeholder,
  mono,
  // Optional native input constraints — surfaced so callers can pin a
  // date input to "today only" for non-admin roles, etc. without forking
  // the component.
  min,
  max,
  disabled,
  // Optional small helper text shown below the input (e.g. "✓ Auto-filled").
  hint,
}) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        {label}
      </label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink ${
          mono ? "font-mono" : ""
        } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      />
      {hint ? (
        <div className="mt-1 font-mono text-[10px] text-ink-subtle">{hint}</div>
      ) : null}
    </div>
  );
}

export function FormTextarea({ label, value, onChange, testId, rows = 3 }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        {label}
      </label>
      <textarea
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
      />
    </div>
  );
}

export function FormSelect({ label, value, onChange, options, testId, disabled }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        {label}
      </label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink ${
          disabled ? "opacity-60 cursor-not-allowed" : ""
        }`}
      >
        {options.map((opt) =>
          typeof opt === "string" ? (
            <option key={opt} value={opt}>
              {opt[0].toUpperCase() + opt.slice(1)}
            </option>
          ) : (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          )
        )}
      </select>
    </div>
  );
}

export function getSaveLabel(saving, editing, savingText, editText, addText) {
  if (saving) return savingText;
  return editing ? editText : addText;
}

export default function Drawer({ open, onClose, title, eyebrow, children, footer, testIdClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-xl bg-canvas-card h-full overflow-y-auto border-l border-canvas-line">
        <div className="px-6 py-5 border-b border-canvas-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted">
              {eyebrow}
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1">{title}</h2>
          </div>
          <button
            onClick={onClose}
            data-testid={testIdClose}
            className="p-2 border border-canvas-line hover:border-ink"
            aria-label="Close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-canvas-line flex items-center gap-2 justify-end">
            {footer}
          </div>
        )}
        {/* Spacer so the "Made with Emergent" platform badge (fixed bottom-right,
            ~64px tall) never overlaps the Save button on smaller viewports. */}
        <div aria-hidden className="h-24" />
      </div>
    </div>
  );
}
